import { Validator } from '@givebacks/utils';
import { isEmail, isNotEmpty, useForm } from '@mantine/form';

import { Alert } from '../../../Feedback';
import { TextInput } from '../../../Inputs/TextInput';
import { Grid } from '../../../Layout';
import { Stack } from '../../../Layout/Stack';

function phoneNumberDisplayFormat(phoneNumberString: string) {
  const cleaned = phoneNumberString.replace(/^1|\D/g, '');
  const res = `${cleaned.slice(0, 3)}${cleaned.length > 3 ? '-' : ''}${cleaned.slice(3, 6)}${
    cleaned.length > 6 ? '-' : ''
  }${cleaned.slice(6)}`;
  return res;
}

type UserInfoFormType = {
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
};
const errorMessages = {
  first_name: 'First Name is required',
  last_name: 'Last Name is required',
  email: 'A valid email is required',
  phone_number: 'A valid phone number is required',
};

const UserInfoForm = ({
  id,
  required,
  isPhoneNumberRequired = false,
  useFormListValidator,
  initialValues,
}: {
  id: string;
  required: boolean;
  isPhoneNumberRequired?: boolean;
  useFormListValidator?: <TForm extends ReturnType<typeof useForm<any>>>(args: {
    form: TForm;
    id: string;
  }) => void;
  initialValues?: Partial<UserInfoFormType>;
}) => {
  const form = useForm<UserInfoFormType>({
    initialValues: {
      first_name: initialValues?.first_name ?? '',
      last_name: initialValues?.last_name ?? '',
      phone_number: '',
      email: initialValues?.email ?? '',
    },

    validate: {
      first_name: required ? isNotEmpty('') : undefined,
      last_name: required ? isNotEmpty('') : undefined,
      phone_number: (value) => {
        // no value, not required, no validaiton
        if (!value && !isPhoneNumberRequired) return null;

        // Always validate values when present
        if (value) {
          const isValid = Validator.phoneNumber(value);
          return isValid ? isValid : null;
        } else {
          // if value is empty and required, show error
          return 'Phone Number is required';
        }
      },

      email: required ? isEmail() : undefined,
    },
    validateInputOnBlur: true,
  });

  const errorOne = Object.keys(form.errors)[0];
  useFormListValidator?.({ form, id });
  return (
    <Stack>
      <Grid grow gutter={8}>
        <Grid.Col span={6}>
          <TextInput placeholder="First Name" {...form.getInputProps('first_name')} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput placeholder="Last Name" {...form.getInputProps('last_name')} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            placeholder={isPhoneNumberRequired ? 'Phone Number' : 'Phone Number (optional)'}
            {...form.getInputProps('phone_number')}
            onChange={(event) => {
              const formattedField = phoneNumberDisplayFormat(event.target.value);
              form.setFieldValue('phone_number', formattedField);
            }}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput placeholder="Email" {...form.getInputProps('email')} />
        </Grid.Col>
      </Grid>
      <Alert.Transition title={errorMessages[errorOne]} type="error" mounted={Boolean(errorOne)} />
    </Stack>
  );
};

export { UserInfoForm };
export type { UserInfoFormType };
